import React, { useRef, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import type { ActiveJob } from '../types';
import { formatRelativeTime } from '../utils/time';
import { FirstSeenCard } from './FirstSeenCard';

/* ── Helpers ──────────────────────────────────────────────── */

const TERMINAL_STATUSES = new Set(['DONE', 'FAILED', 'CANCELLED', 'UPLOAD_FAILED']);
// UPLOADING is active work (bytes transferring with progress), so it drives the
// minimized spinner and lives in the Active section — not Queued. Only PENDING
// (registered but not yet started) is genuinely queued.
const RUNNING_STATUSES = new Set(['UPLOADING', 'RUNNING', 'CANCELLING']);
const QUEUED_STATUSES = new Set(['PENDING']);
const RECENT_CAP = 10;

const isTerminal = (job: ActiveJob) => TERMINAL_STATUSES.has(job.status);
const isRunning = (job: ActiveJob) => RUNNING_STATUSES.has(job.status);
const isQueued = (job: ActiveJob) => QUEUED_STATUSES.has(job.status);

const isFailedLike = (status: string) =>
    status === 'FAILED' || status === 'UPLOAD_FAILED';

const jobLabel = (job: ActiveJob): string => {
    const path = job.datasetPath || job.metadataPath;
    if (path) {
        const parts = path.split(/[\\/]+/).filter(Boolean);
        if (parts.length) return parts[parts.length - 1];
    }
    return job.jobType ?? 'Job';
};

const jobTypeLabel = (job: ActiveJob): string => {
    switch (job.jobType) {
        case 'inference': return 'Inference';
        case 'ingest': return 'Ingest';
        case 'export': return 'Export';
        case 'reprocess_dataset': return 'Re-analysis';
        case 'reprocess_document': return 'Re-analysis';
        case 'filevine_import': return 'Filevine pull';
        case 'filevine_push': return 'Filevine push';
        default: return job.jobType ?? 'Job';
    }
};

const jobProgress = (job: ActiveJob): number => {
    const v = typeof job.progress === 'number' && !Number.isNaN(job.progress) ? job.progress : 0;
    return Math.round(Math.max(0, Math.min(1, v)) * 100);
};

/* ── Props ────────────────────────────────────────────────── */

export interface JobsIndicatorProps {
    jobs: ActiveJob[];
    onCancel?: (jobId: string) => void;
    onDismiss?: (jobId: string) => void;
    onClearCompleted?: () => void;
}

/* ── Progress Ring ────────────────────────────────────────── */

const ProgressRing: React.FC<{ progress: number; size?: number; stroke?: number }> = ({
    progress, size = 20, stroke = 2.5,
}) => {
    const r = (size - stroke) / 2;
    const c = 2 * Math.PI * r;
    return (
        <svg width={size} height={size} style={{ transform: 'rotate(-90deg)', display: 'block' }}>
            <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#e8e8e8" strokeWidth={stroke} />
            <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#111" strokeWidth={stroke}
                strokeDasharray={c} strokeDashoffset={c - (progress / 100) * c}
                strokeLinecap="round" style={{ transition: 'stroke-dashoffset 0.6s ease' }} />
        </svg>
    );
};

/* ── Status Icons ─────────────────────────────────────────── */

const spin = keyframes`to { transform: rotate(360deg); }`;

const Spinner = styled.div`
    width: 14px;
    height: 14px;
    position: relative;
    flex-shrink: 0;

    svg { position: absolute; top: 0; left: 0; }
    svg:last-child { animation: ${spin} 1.2s linear infinite; transform-origin: center; }
`;

const RunningIcon: React.FC = () => {
    const c = 2 * Math.PI * 5.5;
    return (
        <Spinner>
            <svg width="14" height="14" viewBox="0 0 14 14">
                <circle cx="7" cy="7" r="5.5" fill="none" stroke="#e8e8e8" strokeWidth="2" />
            </svg>
            <svg width="14" height="14" viewBox="0 0 14 14">
                <circle cx="7" cy="7" r="5.5" fill="none" stroke="#111" strokeWidth="2"
                    strokeDasharray={`${c * 0.3} ${c * 0.7}`} strokeLinecap="round" />
            </svg>
        </Spinner>
    );
};

const CompletedIcon: React.FC = () => (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{ flexShrink: 0 }}>
        <circle cx="7" cy="7" r="6" fill="#22c55e" />
        <path d="M4.5 7L6.5 9L9.5 5.5" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const FailedIcon: React.FC = () => (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{ flexShrink: 0 }}>
        <circle cx="7" cy="7" r="6" fill="#ef4444" />
        <path d="M5 5L9 9M9 5L5 9" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
);

const QueuedIcon: React.FC = () => (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{ flexShrink: 0 }}
        aria-label="Queued">
        <circle cx="7" cy="7" r="5.5" fill="none" stroke="#cbd5e1" strokeWidth="1.5"
            strokeDasharray="2 2" />
        <circle cx="7" cy="7" r="1.5" fill="#94a3b8" />
    </svg>
);

const StatusIcon: React.FC<{ status: string }> = ({ status }) => {
    if (status === 'DONE') return <CompletedIcon />;
    if (status === 'FAILED' || status === 'CANCELLED' || status === 'UPLOAD_FAILED') return <FailedIcon />;
    if (status === 'PENDING') return <QueuedIcon />;
    // UPLOADING shows the running indicator — it's active work, not queued.
    return <RunningIcon />;
};

/* ── Idle Icon (no active jobs) ───────────────────────────── */

const IdleIcon: React.FC<{ hasFailed: boolean }> = ({ hasFailed }) => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none"
        stroke={hasFailed ? '#ef4444' : '#bbb'} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M4 4H12V12H4Z" />
        <path d="M6 2V4M10 2V4" />
        <path d="M6 8H10" />
    </svg>
);

/* ── Styled Components ────────────────────────────────────── */

const Wrapper = styled.div`
    position: relative;
`;

const Trigger = styled.div<{ $open: boolean }>`
    display: flex;
    align-items: center;
    gap: 6px;
    cursor: pointer;
    padding: 4px 8px;
    border-radius: 6px;
    background: ${({ $open }) => $open ? '#f0f0f0' : 'transparent'};
    transition: background 0.15s;

    &:hover {
        background: ${({ $open }) => $open ? '#f0f0f0' : '#f8f8f8'};
    }
`;

const ActiveCount = styled.span`
    font-size: 11px;
    font-weight: 600;
    color: #333;
    font-variant-numeric: tabular-nums;
`;

const FailedDot = styled.span`
    width: 5px;
    height: 5px;
    border-radius: 50%;
    background: #ef4444;
`;

const Overlay = styled.div`
    position: fixed;
    inset: 0;
    z-index: 50;
`;

const Panel = styled.div`
    position: absolute;
    top: calc(100% + 8px);
    right: 0;
    z-index: 51;
    width: 360px;
    background: #fff;
    border: 1px solid #e8e8e8;
    border-radius: 10px;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
    overflow: hidden;
`;

const PanelHeader = styled.div`
    padding: 14px 16px 12px;
    border-bottom: 1px solid #f0f0f0;
    display: flex;
    align-items: center;
    justify-content: space-between;
`;

const PanelTitle = styled.span`
    font-size: 13px;
    font-weight: 600;
    color: #111;
`;

const PanelSummary = styled.span`
    font-size: 11px;
    color: #999;
    font-weight: 500;
`;

const PanelList = styled.div`
    max-height: 360px;
    overflow-y: auto;
`;

const SectionHeader = styled.div`
    padding: 8px 16px 4px;
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    color: #999;
    background: #fafafa;
    border-bottom: 1px solid #f4f4f4;
`;

const QueuePosition = styled.span`
    font-size: 11px;
    color: #bbb;
    font-variant-numeric: tabular-nums;
    margin-right: 6px;
`;

const JobRow = styled.div`
    padding: 12px 16px;
    border-bottom: 1px solid #f8f8f8;
    display: flex;
    gap: 10px;
    align-items: flex-start;

    &:hover {
        background: #fafafa;
    }
`;

const JobIconCell = styled.div`
    padding-top: 2px;
    flex-shrink: 0;
    width: 14px;
    height: 14px;
`;

const JobContent = styled.div`
    flex: 1;
    min-width: 0;
`;

const JobHeader = styled.div`
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 8px;
`;

const JobType = styled.span`
    font-size: 13px;
    font-weight: 500;
    color: #222;
`;

const JobTime = styled.span`
    font-size: 11px;
    color: #ccc;
    flex-shrink: 0;
`;

const JobTarget = styled.div`
    font-size: 12px;
    color: #888;
    margin-top: 1px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const JobMessage = styled.div`
    font-size: 11px;
    color: #999;
    margin-top: 2px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const JobFilename = styled.div`
    font-size: 11px;
    color: #777;
    margin-top: 1px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const JobError = styled.div`
    font-size: 11px;
    color: #c0392b;
    margin-top: 2px;
    word-break: break-word;
`;

const JobProgressRow = styled.div`
    margin-top: 6px;
    display: flex;
    align-items: center;
    gap: 8px;
`;

const ProgressTrack = styled.div`
    flex: 1;
    height: 3px;
    background: #f0f0f0;
    border-radius: 2px;
    overflow: hidden;
`;

const ProgressFill = styled.div<{ $percent: number }>`
    width: ${({ $percent }) => $percent}%;
    height: 100%;
    background: #111;
    border-radius: 2px;
    transition: width 0.6s ease;
`;

const ProgressPercent = styled.span`
    font-size: 11px;
    color: #999;
    font-variant-numeric: tabular-nums;
    min-width: 28px;
    text-align: right;
`;

const ProgressMeta = styled.div`
    margin-top: 4px;
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    gap: 8px;
    font-size: 11px;
    color: #999;
`;

const StepText = styled.span`
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const EtaText = styled.span`
    flex-shrink: 0;
    font-variant-numeric: tabular-nums;
`;

const StepList = styled.ul`
    margin: 6px 0 0;
    padding: 0;
    list-style: none;
    display: flex;
    flex-direction: column;
    gap: 2px;
`;

const StepItem = styled.li<{ $state: 'done' | 'current' | 'pending' }>`
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 11px;
    color: ${({ $state }) => ($state === 'current' ? '#111' : $state === 'done' ? '#888' : '#bbb')};
    font-weight: ${({ $state }) => ($state === 'current' ? 600 : 400)};
`;

const StepDot = styled.span<{ $state: 'done' | 'current' | 'pending' }>`
    width: 6px;
    height: 6px;
    border-radius: 50%;
    flex-shrink: 0;
    background: ${({ $state }) => ($state === 'done' ? '#22c55e' : $state === 'current' ? '#111' : '#ddd')};
`;

/** "2m 10s", "45s", "~1h 3m". null/very-large -> null (don't show noise). */
const formatEta = (seconds: number | null | undefined): string | null => {
    if (seconds == null || !Number.isFinite(seconds) || seconds < 0) return null;
    const s = Math.round(seconds);
    if (s < 1) return 'almost done';
    if (s < 60) return `${s}s left`;
    const m = Math.floor(s / 60);
    if (m < 60) return `${m}m ${s % 60}s left`;
    const h = Math.floor(m / 60);
    return `${h}h ${m % 60}m left`;
};

const FailedActions = styled.div`
    margin-top: 4px;
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 11px;
`;

const ActionLink = styled.span<{ $color?: string }>`
    font-weight: 600;
    color: ${({ $color }) => $color ?? '#bbb'};
    cursor: pointer;

    &:hover { text-decoration: underline; }
`;

const ActionDot = styled.span`
    color: #ddd;
`;

const PanelFooter = styled.div`
    padding: 10px 16px;
    border-top: 1px solid #f0f0f0;
    display: flex;
    justify-content: center;
`;

const FooterLink = styled.span`
    font-size: 12px;
    color: #999;
    cursor: pointer;
    font-weight: 500;

    &:hover { color: #333; }
`;

/* ── Component ────────────────────────────────────────────── */

interface JobRowViewProps {
    job: ActiveJob;
    queuePosition?: number;
    onCancel?: (jobId: string) => void;
    onDismiss?: (jobId: string) => void;
}

const JobRowView: React.FC<JobRowViewProps> = ({ job, queuePosition, onCancel, onDismiss }) => {
    // Overall progress (weighted across all steps) is the headline bar; fall back
    // to current-phase progress for jobs that don't report an overall value.
    const overall = jobProgress(job);
    const progress = overall > 0
        ? overall
        : (job.phaseProgress != null ? Math.round(Math.max(0, Math.min(1, job.phaseProgress)) * 100) : 0);
    const steps = job.steps ?? null;
    const currentStep = job.currentStep ?? null;
    const eta = formatEta(job.etaSeconds);
    const showProgress = !isTerminal(job) && !isQueued(job) && (overall > 0 || job.phaseProgress != null);
    const target = jobLabel(job);
    const updatedAt = job.updatedAt ?? job.createdAt;
    const queued = isQueued(job);
    const cancelling = job.status === 'CANCELLING';

    return (
        <JobRow>
            <JobIconCell>
                <StatusIcon status={job.status} />
            </JobIconCell>
            <JobContent>
                <JobHeader>
                    <JobType>
                        {queuePosition != null && <QueuePosition>#{queuePosition}</QueuePosition>}
                        {jobTypeLabel(job)}
                    </JobType>
                    <JobTime>{updatedAt ? formatRelativeTime(updatedAt) : ''}</JobTime>
                </JobHeader>
                <JobTarget>{target}</JobTarget>
                {job.sourceFilename && (
                    <JobFilename title={job.sourceFilename}>📄 {job.sourceFilename}</JobFilename>
                )}
                {!isTerminal(job) && job.message && (
                    <JobMessage>{job.message}</JobMessage>
                )}
                {isFailedLike(job.status) && job.error && (
                    <JobError title={job.error}>{job.error}</JobError>
                )}

                {showProgress && (
                    <>
                        <JobProgressRow>
                            <ProgressTrack>
                                <ProgressFill $percent={progress} />
                            </ProgressTrack>
                            <ProgressPercent>{progress}%</ProgressPercent>
                        </JobProgressRow>
                        {(steps?.length || eta) && (
                            <ProgressMeta>
                                <StepText>
                                    {steps?.length && currentStep != null
                                        ? `Step ${Math.min(currentStep + 1, steps.length)} of ${steps.length}`
                                        : ''}
                                </StepText>
                                {eta && <EtaText>{eta}</EtaText>}
                            </ProgressMeta>
                        )}
                        {steps && steps.length > 1 && currentStep != null && (
                            <StepList>
                                {steps.map((label, i) => {
                                    const state = i < currentStep ? 'done' : i === currentStep ? 'current' : 'pending';
                                    return (
                                        <StepItem key={`${i}-${label}`} $state={state}>
                                            <StepDot $state={state} />
                                            {label}
                                        </StepItem>
                                    );
                                })}
                            </StepList>
                        )}
                    </>
                )}

                {!isTerminal(job) && onCancel && (
                    <FailedActions>
                        {cancelling ? (
                            <ActionLink as="span" $color="#999" style={{ cursor: 'default' }}>
                                Cancelling…
                            </ActionLink>
                        ) : queued ? (
                            <ActionLink $color="#bbb" onClick={() => onCancel(job.jobId)}>
                                Remove from queue
                            </ActionLink>
                        ) : (
                            <ActionLink $color="#bbb" onClick={() => onCancel(job.jobId)}>
                                Cancel
                            </ActionLink>
                        )}
                    </FailedActions>
                )}

                {isFailedLike(job.status) && onDismiss && (
                    <FailedActions>
                        <ActionLink $color="#bbb" onClick={() => onDismiss(job.jobId)}>Dismiss</ActionLink>
                    </FailedActions>
                )}
            </JobContent>
        </JobRow>
    );
};

export const JobsIndicator: React.FC<JobsIndicatorProps> = ({
    jobs, onCancel, onDismiss, onClearCompleted,
}) => {
    const [open, setOpen] = useState(false);
    const wrapperRef = useRef<HTMLDivElement | null>(null);

    // Backend returns rows in queue order; we just split by status.
    // The first prefix is the active and queued sections; everything else
    // is terminal (recently completed/failed/cancelled).
    const running = jobs.filter(isRunning);
    const queued = jobs.filter(isQueued);
    const recent = jobs.filter(isTerminal).slice(0, RECENT_CAP);

    const failed = jobs.filter(j => isFailedLike(j.status));
    const hasCompleted = jobs.some(j => j.status === 'DONE' || j.status === 'CANCELLED');
    const hasActive = running.length > 0;
    const hasFailed = failed.length > 0;

    const avgProgress = hasActive
        ? Math.round(running.reduce((s, j) => {
            // Prefer the weighted overall progress; fall back to current-phase.
            const overall = jobProgress(j);
            const pp = overall > 0
                ? overall
                : (j.phaseProgress != null ? Math.round(j.phaseProgress * 100) : 0);
            return s + pp;
        }, 0) / running.length)
        : 100;

    if (jobs.length === 0) return null;

    return (
        <Wrapper ref={wrapperRef}>
            <Trigger $open={open} onClick={() => setOpen(o => !o)}>
                {hasActive ? (
                    <>
                        <ProgressRing progress={avgProgress} />
                        <ActiveCount>{running.length}</ActiveCount>
                    </>
                ) : (
                    <>
                        <IdleIcon hasFailed={hasFailed} />
                        {hasFailed && <FailedDot />}
                    </>
                )}
            </Trigger>

            {open && (
                <>
                    <Overlay onClick={() => setOpen(false)} />
                    <Panel>
                        <PanelHeader>
                            <PanelTitle>Jobs</PanelTitle>
                            <PanelSummary>
                                {hasActive
                                    ? `${running.length} running, ${queued.length} queued`
                                    : queued.length > 0
                                        ? `${queued.length} queued`
                                        : `${jobs.length} total`}
                            </PanelSummary>
                        </PanelHeader>

                        <PanelList>
                            {running.length > 0 && (
                                <>
                                    <SectionHeader>Active</SectionHeader>
                                    {running.map(job => (
                                        <JobRowView
                                            key={job.jobId}
                                            job={job}
                                            onCancel={onCancel}
                                        />
                                    ))}
                                </>
                            )}

                            {queued.length > 0 && (
                                <>
                                    <SectionHeader>Queued</SectionHeader>
                                    {queued.map((job, idx) => (
                                        <JobRowView
                                            key={job.jobId}
                                            job={job}
                                            queuePosition={idx + 1}
                                            onCancel={onCancel}
                                        />
                                    ))}
                                </>
                            )}

                            {recent.length > 0 && (
                                <>
                                    <SectionHeader>Recent</SectionHeader>
                                    {recent.map(job => (
                                        <JobRowView
                                            key={job.jobId}
                                            job={job}
                                            onDismiss={onDismiss}
                                        />
                                    ))}
                                </>
                            )}
                        </PanelList>

                        {hasCompleted && onClearCompleted && (
                            <PanelFooter>
                                <FooterLink onClick={onClearCompleted}>Clear completed</FooterLink>
                            </PanelFooter>
                        )}
                    </Panel>
                </>
            )}
            <FirstSeenCard
                cardKey="jobs_indicator"
                targetRef={wrapperRef}
                title="This is the job queue"
                placement="bottom"
            >
                <p>
                    Anything you upload, re-analyze, or export shows up here.
                </p>
                <ul>
                    <li>Click to expand — see active jobs, queued ones, and recent results.</li>
                    <li>Cancel a job from the same panel.</li>
                </ul>
            </FirstSeenCard>
        </Wrapper>
    );
};
